import { LanguageModelProvider } from "./utils/aiUtils";

export interface AIAppConfig {
  googleApiKey: string;
  googleScoringModel: string;

  anthropicApiKey: string;
  anthropicScoringModel: string;

  openaiApiKey: string;
  openaiBaseUrl: string;
  openaiRealtimeModel: string;
  openaiScoringModel: string;
  openaiDefaultVoiceName: string;
  openaiModel: string;
  openaiTTSVoiceIDDefault: string;
  openaiTTSModel: string;

  elevenLabsApiKey: string;
  elevenLabsVoiceIDDefault: string;

  provider: LanguageModelProvider;
  scoringTemperature?: number | null;
}

export interface UIAppConfig {
  showMobileScrollbarsInDevelopmentEnv: boolean;
  audioCheckHelpLinkUrl: string;
}

export interface AnalyticsConfig {
  enabled: boolean;
  environment: string;
  posthogKey: string;
  posthogHost: string;
  serverLoggingFlag?: string; // server-only — only populated via serverAppConfig
}

export interface DebateMateConfig {
  maxSkillLevel: number;
  limits: {
    perUserLSASessionLimit: number;
    perUserLSASessionLimitPeriodDays: number;
  };
}

export interface AppConfig {
  apiBasePath: string;
  uploadBasePath: string; // Server-specific, placeholder value
  publicBasePath: string; // Server-specific, placeholder value
  databaseUri: string;
  analytics: AnalyticsConfig;
  ai: AIAppConfig;
  ui: UIAppConfig;
  dm: DebateMateConfig;
  version: string;
  environment: string;
}

/** Cohort reports bucket sessions into weeks in this timezone, for every cohort. */
export const reportTimezone = "Europe/London";

export const appConfig: AppConfig = {
  apiBasePath: process.env.NEXT_PUBLIC_API_BASE_PATH || "/api",
  uploadBasePath: "SERVER_ONLY_PLACEHOLDER", // Placeholder to indicate server-only
  publicBasePath: "SERVER_ONLY_PLACEHOLDER", // Placeholder to indicate server-only
  databaseUri: process.env.DATABASE_URI || "",
  dm: {
    maxSkillLevel: Number.parseInt(process.env.MAXIMUM_SKILL_LEVEL || "25"),
    limits: {
      perUserLSASessionLimit: Number.parseInt(process.env.LSA_SESSION_LIMIT || "50"),
      perUserLSASessionLimitPeriodDays: Number.parseInt(process.env.LSA_SESSION_LIMIT_PERIOD_DAYS || "7"),
    },
  },
  analytics: {
    enabled: process.env.NEXT_PUBLIC_ANALYTICS_ENABLED === "true" || false,
    environment: process.env.NEXT_PUBLIC_POSTHOG_ENVIRONMENT || "",
    posthogKey: process.env.NEXT_PUBLIC_POSTHOG_KEY || "",
    posthogHost: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://eu.i.posthog.com",
  },
  ai: {
    provider: (process.env.AI_PROVIDER as LanguageModelProvider) || "openai",
    scoringTemperature: process.env.SCORING_TEMPERATURE
      ? Number.parseFloat(process.env.SCORING_TEMPERATURE)
      : undefined,

    openaiApiKey: process.env.OPENAI_API_KEY || "<no_env_found>",
    openaiBaseUrl: process.env.OPENAI_BASE_URL || "https://api.openai.com/v1",
    openaiDefaultVoiceName: process.env.OPENAI_DEFAULT_VOICE_NAME || "verse",
    openaiRealtimeModel: process.env.OPENAI_REALTIME_MODEL || "<no_env_found>",
    openaiModel: process.env.OPENAI_MODEL || "<no_env_found>",
    openaiScoringModel: process.env.OPENAI_SCORING_MODEL || "<no_env_found>",
    openaiTTSVoiceIDDefault: process.env.OPENAI_TTS_VOICE_ID_DEFAULT || "",
    openaiTTSModel: process.env.OPENAI_TTS_MODEL || "<no_env_found>",

    elevenLabsApiKey: process.env.ELEVEN_LABS_API_KEY || "<no_env_found>",
    elevenLabsVoiceIDDefault: process.env.ELEVEN_LABS_VOICE_ID_DEFAULT || "",

    googleApiKey: process.env.GOOGLE_API_KEY || "<no_env_found>",
    googleScoringModel: process.env.GOOGLE_SCORING_MODEL || "<no_env_found>",

    anthropicApiKey: process.env.ANTHROPIC_API_KEY || "<no_env_found>",
    anthropicScoringModel: process.env.ANTHROPIC_SCORING_MODEL || "<no_env_found>",
  },
  ui: {
    // Set to true to aid in local development and design/layouting
    showMobileScrollbarsInDevelopmentEnv: false,
    audioCheckHelpLinkUrl: "https://google.com?q=how+to+fix+my+audio+on+my+computer",
  },
  version: process.env.NEXT_PUBLIC_VERSION || "",
  environment: process.env.NEXT_PUBLIC_DEB_ENVIRONMENT || "unknown",
};

export default appConfig;
